import type { GencowProcedureDef, QueryDef } from "@gencow/core";
import { getQueryDef, getRegisteredMutations, getRegisteredQueries } from "@gencow/core";
import { schemaToCodegenTypeLiteral } from "./introspection/codegen-schema-types.js";
import { collectProcedureRouteMaps } from "./procedure-route-maps.js";

type LegacyMutationLike = {
  name: string;
  isPublic?: boolean;
  argsSchema?: unknown;
  invalidates?: unknown;
};

type ProcedureMaps = {
  queryProcedureMap: Map<string, GencowProcedureDef<"query", any, any, any>>;
  mutationProcedureMap: Map<string, GencowProcedureDef<"mutation", any, any, any>>;
};

export type AdminOperationDescriptor = {
  key: string;
  kind: "query" | "mutation";
  transport: "legacy" | "procedure";
  allowAnonymous: boolean;
  invalidates?: string[];
  argsType: string;
  outputType: string;
  hasInputSchema: boolean;
  hasOutputSchema: boolean;
  inputSchema?: unknown;
  outputSchema?: unknown;
};

export type AdminFunctionDetailsResponse = {
  operations: AdminOperationDescriptor[];
  totals: {
    queries: number;
    mutations: number;
    operations: number;
  };
};

type JsonLike = string | number | boolean | null | JsonLike[] | { [key: string]: JsonLike };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function toSerializableJson(value: unknown, seen = new WeakSet<object>()): JsonLike | undefined {
  if (value === null || typeof value === "string" || typeof value === "boolean") return value;
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value !== "object") return undefined;
  if (seen.has(value)) return undefined;
  seen.add(value);

  if (Array.isArray(value)) {
    const entries: JsonLike[] = [];
    for (const entry of value) {
      const serialized = toSerializableJson(entry, seen);
      if (serialized === undefined) return undefined;
      entries.push(serialized);
    }
    return entries;
  }

  if (!isPlainObject(value)) return undefined;

  const output: Record<string, JsonLike> = {};
  for (const [key, entry] of Object.entries(value)) {
    const serialized = toSerializableJson(entry, seen);
    if (serialized === undefined) return undefined;
    output[key] = serialized;
  }
  return output;
}

function withSerializableSchema<T extends AdminOperationDescriptor>(
  descriptor: T,
  inputSchema: unknown,
  outputSchema: unknown,
): T {
  const serializableInput = toSerializableJson(inputSchema);
  const serializableOutput = toSerializableJson(outputSchema);
  return {
    ...descriptor,
    ...(serializableInput !== undefined ? { inputSchema: serializableInput } : {}),
    ...(serializableOutput !== undefined ? { outputSchema: serializableOutput } : {}),
  };
}

function toInvalidates(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const invalidates = value.filter((entry): entry is string => typeof entry === "string");
  return invalidates.length > 0 ? invalidates : undefined;
}

function sortOperations(operations: AdminOperationDescriptor[]): AdminOperationDescriptor[] {
  const kindRank = { query: 0, mutation: 1 };
  return [...operations].sort((a, b) => {
    const byKind = kindRank[a.kind] - kindRank[b.kind];
    if (byKind !== 0) return byKind;
    return a.key.localeCompare(b.key);
  });
}

export function collectAdminFunctionDetails(
  params: {
    appModule?: unknown;
    procedureMaps?: ProcedureMaps;
    getRegisteredQueries?: () => string[];
    getQueryDef?: (name: string) => QueryDef | undefined;
    getRegisteredMutations?: () => LegacyMutationLike[];
    collectProcedureRouteMaps?: typeof collectProcedureRouteMaps;
  } = {},
): AdminFunctionDetailsResponse {
  const registeredQueries = params.getRegisteredQueries ?? getRegisteredQueries;
  const queryDefFor = params.getQueryDef ?? getQueryDef;
  const registeredMutations = params.getRegisteredMutations ?? getRegisteredMutations;
  const procedureMaps =
    params.procedureMaps ?? (params.collectProcedureRouteMaps ?? collectProcedureRouteMaps)(params.appModule);

  const operations: AdminOperationDescriptor[] = [];

  for (const key of registeredQueries()) {
    const queryDef = queryDefFor(key);
    const inputSchema = queryDef?.argsSchema;
    operations.push(
      withSerializableSchema(
        {
          key,
          kind: "query",
          transport: "legacy",
          allowAnonymous: queryDef?.isPublic === true,
          argsType: schemaToCodegenTypeLiteral(inputSchema),
          outputType: "unknown",
          hasInputSchema: inputSchema !== undefined,
          hasOutputSchema: false,
        },
        inputSchema,
        undefined,
      ),
    );
  }

  for (const mutation of registeredMutations()) {
    const inputSchema = mutation.argsSchema;
    operations.push(
      withSerializableSchema(
        {
          key: mutation.name,
          kind: "mutation",
          transport: "legacy",
          allowAnonymous: mutation.isPublic === true,
          invalidates: toInvalidates((mutation as LegacyMutationLike).invalidates),
          argsType: schemaToCodegenTypeLiteral(inputSchema),
          outputType: "unknown",
          hasInputSchema: inputSchema !== undefined,
          hasOutputSchema: false,
        },
        inputSchema,
        undefined,
      ),
    );
  }

  for (const [key, procedure] of procedureMaps.queryProcedureMap.entries()) {
    const inputSchema = procedure.inputSchema;
    const outputSchema = procedure.outputSchema;
    operations.push(
      withSerializableSchema(
        {
          key,
          kind: "query",
          transport: "procedure",
          allowAnonymous: procedure.allowAnonymous === true,
          argsType: schemaToCodegenTypeLiteral(inputSchema),
          outputType: schemaToCodegenTypeLiteral(outputSchema),
          hasInputSchema: inputSchema !== undefined,
          hasOutputSchema: outputSchema !== undefined,
        },
        inputSchema,
        outputSchema,
      ),
    );
  }

  for (const [key, procedure] of procedureMaps.mutationProcedureMap.entries()) {
    const inputSchema = procedure.inputSchema;
    const outputSchema = procedure.outputSchema;
    operations.push(
      withSerializableSchema(
        {
          key,
          kind: "mutation",
          transport: "procedure",
          allowAnonymous: procedure.allowAnonymous === true,
          argsType: schemaToCodegenTypeLiteral(inputSchema),
          outputType: schemaToCodegenTypeLiteral(outputSchema),
          hasInputSchema: inputSchema !== undefined,
          hasOutputSchema: outputSchema !== undefined,
        },
        inputSchema,
        outputSchema,
      ),
    );
  }

  const sortedOperations = sortOperations(operations);
  const queryCount = sortedOperations.filter((operation) => operation.kind === "query").length;
  const mutationCount = sortedOperations.length - queryCount;

  return {
    operations: sortedOperations,
    totals: {
      queries: queryCount,
      mutations: mutationCount,
      operations: sortedOperations.length,
    },
  };
}
